/**
 * Email Processor Page
 *
 * Main page for Email Attachment Processor feature
 * Provides email configuration, thread viewing, and statistics
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  Settings as SettingsIcon,
  Assessment as StatsIcon,
} from '@mui/icons-material';

import PageTitleBar from '../components/PageTitleBar';
import EmailConfigList from '../components/EmailProcessor/EmailConfigList';
import EmailThreadList from '../components/EmailProcessor/EmailThreadList';
import ProcessingStatsCard from '../components/EmailProcessor/ProcessingStatsCard';
import { useTranslation } from '../store/languageStore';
import { brand } from '../theme';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`email-processor-tabpanel-${index}`}
      aria-labelledby={`email-processor-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const EmailProcessor: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(1); // Start with Threads tab (Configuration tab is hidden)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ height: '100%', bgcolor: 'grey.50', overflow: 'auto' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <PageTitleBar
          title={t('emailProcessorPage.title') as string}
          subtitle={t('emailProcessorPage.subtitle') as string}
        />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={1} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: `1px solid ${brand.borderSubtle}`,
            '& .MuiTabs-indicator': { backgroundColor: brand.orange },
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
            '& .Mui-selected': { color: `${brand.orange} !important` },
          }}
        >
          <Tab
            icon={<SettingsIcon />}
            label={t('emailProcessorPage.tabs.configuration')}
            id="email-processor-tab-0"
            aria-controls="email-processor-tabpanel-0"
            sx={{ display: 'none' }}
          />
          <Tab
            icon={<EmailIcon />}
            label={t('emailProcessorPage.tabs.threads')}
            id="email-processor-tab-1"
            aria-controls="email-processor-tabpanel-1"
          />
          <Tab
            icon={<StatsIcon />}
            label={t('emailProcessorPage.tabs.statistics')}
            id="email-processor-tab-2"
            aria-controls="email-processor-tabpanel-2"
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <EmailConfigList onError={setError} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <EmailThreadList onError={setError} />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <ProcessingStatsCard onError={setError} />
      </TabPanel>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      </Container>
    </Box>
  );
};

export default EmailProcessor;
